import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useFilms } from "../../context/FilmsContext";

const GENRES = [
  {
    id: "horror",
    name: "HORROR",
    value: "Horror",
    tag: "Shadows & Screams",
    desc: "Vampires, somnambulists, zombies and the undead. The films that invented dread.",
    img: "https://static.prod-images.emergentagent.com/jobs/86fb1679-d749-4b31-9538-591f84a31f12/images/8115e57821c93f405919aa9156c35703cc4ff710e051fe9103487c9eb743bd2d.jpeg",
  },
  {
    id: "scifi",
    name: "SCI-FI",
    value: "Sci-Fi",
    tag: "Rockets & Robots",
    desc: "Moon voyages, metal cities and cosmic invaders. Yesterday's tomorrow, restored.",
    img: "https://static.prod-images.emergentagent.com/jobs/86fb1679-d749-4b31-9538-591f84a31f12/images/813d81a42738aa88497176393eb0b637df2529d4a7f26bcebe271775adf3fe67.jpeg",
  },
  {
    id: "comedy",
    name: "COMEDY",
    value: "Comedy",
    tag: "Slapstick & Chaos",
    desc: "The Stooges, Keaton, Chaplin and Abbott & Costello. Anarchy that never went out of copyright.",
    img: "https://static.prod-images.emergentagent.com/jobs/86fb1679-d749-4b31-9538-591f84a31f12/images/276bc834685c593b7b6df0b46df9faf05b63ee60d4ae428e11aafec822ff0507.jpeg",
  },
];

const GenreRow = ({ genre, index }) => {
  const [hover, setHover] = useState(false);
  const navigate = useNavigate();
  const { films } = useFilms();
  const count = films.filter((f) => f.category === genre.value).length;
  return (
    <motion.button
      data-testid={`genre-${genre.id}`}
      onClick={() => navigate(`/archive?genre=${encodeURIComponent(genre.value)}`)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: index * 0.1 }}
      className="group relative w-full text-left border-b border-white/10 first:border-t overflow-hidden cursor-pointer"
    >
      {/* revealing image */}
      <div
        className={`pointer-events-none absolute right-6 md:right-24 top-1/2 -translate-y-1/2 h-40 w-64 md:h-56 md:w-96 overflow-hidden transition-all duration-500 ease-out z-0 ${
          hover ? "opacity-100 scale-100" : "opacity-0 scale-90"
        }`}
      >
        <img
          src={genre.img}
          alt=""
          className="h-full w-full object-cover grayscale contrast-125"
        />
        <div className="absolute inset-0 scanlines opacity-60" />
        <div className="absolute inset-0 bg-blood/20 mix-blend-multiply" />
      </div>

      <div className="relative z-10 py-10 md:py-16 px-1 md:px-4 flex items-center justify-between">
        <div className="flex items-baseline gap-4 md:gap-8">
          <span className="font-mono text-[11px] md:text-sm tracking-[0.3em] text-blood">
            0{index + 1}
          </span>
          <h3
            className={`font-serif text-6xl md:text-8xl lg:text-9xl leading-none tracking-tighter transition-colors duration-500 ${
              hover ? "text-blood" : "text-bone"
            }`}
            style={{
              WebkitTextStroke: hover ? "0px" : "0px",
            }}
          >
            {genre.name}
          </h3>
        </div>
        <div className="hidden lg:flex flex-col items-end gap-2 z-10">
          <span className="font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground">
            {count} titles
          </span>
          <ArrowUpRight
            className={`h-8 w-8 transition-transform duration-500 ${
              hover ? "rotate-0 text-blood translate-x-1 -translate-y-1" : "text-bone"
            }`}
          />
        </div>
      </div>
    </motion.button>
  );
};

export const Categories = () => {
  return (
    <section
      id="genres"
      data-testid="genres-section"
      className="relative py-24 md:py-36 px-5 md:px-10 bg-black"
    >
      <div className="max-w-[1600px] mx-auto">
        <div className="mb-10 md:mb-14 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <span className="font-mono text-[11px] tracking-[0.4em] uppercase text-blood">
              (Three Genres, One Vault)
            </span>
            <h2 className="mt-4 font-serif text-5xl md:text-7xl leading-[0.9] tracking-tighter text-bone">
              Pick your <span className="italic">poison</span>
            </h2>
          </div>
          <p className="max-w-xs text-sm md:text-base text-muted-foreground leading-relaxed">
            Everything here is out of copyright and into your hands. Hover to
            look inside.
          </p>
        </div>

        <div>
          {GENRES.map((g, i) => (
            <GenreRow key={g.id} genre={g} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
